import React, { Fragment, useState } from 'react';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';
import { css } from '@emotion/core';
import { withTheme } from 'emotion-theming';

const circleStyles = () => css`
  img {
    border-radius: 9999px;
  }
`;

const StyledPicture = styled.picture`
  display: block;
  position: relative;

  img {
    height: auto;
    width: 100%;
    display: block;
  }

  ${(props) => (props.circle ? circleStyles : '')};
`;

const Picture = (props) => {
  const [imageHost] = useState(props.uri ? process.env.NEXT_PUBLIC_ASSET_HOST : '');

  const imageStack = () => {
    const media = props.theme.breakpoint;
    const previewScrset = () => `${imageHost}/${props.uri}${props.imageName}`;

    const srcset = (size, type) => `
      ${imageHost}/${props.uri}${props.imageName.split('.')[0]}-${size}-super-retina.${type} 3x,
      ${imageHost}/${props.uri}${props.imageName.split('.')[0]}-${size}-retina.${type} 2x,
      ${imageHost}/${props.uri}${props.imageName.split('.')[0]}-${size}.${type}
    `;

    if (props.raw || !props.uri) {
      const dynamicAttributesObj = {};
      if (props.disableLazyload) {
        dynamicAttributesObj.srcSet = props.flickity ? undefined : previewScrset();
      } else {
        dynamicAttributesObj['data-srcset'] = props.flickity ? undefined : previewScrset();
      }
      return (
        <Fragment key={previewScrset()}>
          <source
            type={`${props.types[0]}`}
            media={`(max-width: ${media.max('small')})`}
            data-flickity-lazyload-srcset={!props.flickity ? undefined : previewScrset()}
            {...dynamicAttributesObj}
          />
        </Fragment>
      );
    }

    function dynamicAttributes(size, type) {
      if (props.disableLazyload) {
        return {
          srcSet: props.flickity ? undefined : srcset(size, type)
        };
      }
      return {
        'data-srcset': props.flickity ? undefined : srcset(size, type)
      };
    }

    if (props.size === 'small') {
      return props.types.map((type) => (
        <source
          key={srcset('small', type)}
          type={`image/${type}`}
          data-flickity-lazyload-srcset={!props.flickity ? undefined : srcset('small', type)}
          {...dynamicAttributes('small', type)}
        />
      ));
    }

    if (props.size === 'medium') {
      return props.types.map((type) => (
        <Fragment key={srcset('medium', type)}>
          <source
            type={`image/${type}`}
            media={`(max-width: ${media.max('small')})`}
            data-flickity-lazyload-srcset={!props.flickity ? undefined : srcset('small', type)}
            {...dynamicAttributes('small', type)}
          />

          <source
            type={`image/${type}`}
            media={`(min-width: ${media.small}))`}
            data-flickity-lazyload-srcset={!props.flickity ? undefined : srcset('medium', type)}
            {...dynamicAttributes('medium', type)}
          />
        </Fragment>
      ));
    }

    return props.types.map((type) => (
      <Fragment key={srcset('medium', type)}>
        <source
          type={`image/${type}`}
          media={`(max-width: ${media.max('small')})`}
          data-flickity-lazyload-srcset={!props.flickity ? undefined : srcset('small', type)}
          {...dynamicAttributes('small', type)}
        />

        <source
          type={`image/${type}`}
          media={`(min-width: ${media.small}) and (max-width: ${media.max('medium')})`}
          data-flickity-lazyload-srcset={!props.flickity ? undefined : srcset('medium', type)}
          {...dynamicAttributes('medium', type)}
        />

        <source
          type={`image/${type}`}
          media={`(min-width: ${media.medium})`}
          data-flickity-lazyload-srcset={!props.flickity ? undefined : srcset('large', type)}
          {...dynamicAttributes('large', type)}
        />
      </Fragment>
    ));
  };

  const defaultImage = () => {
    const type = props.types[props.types.length - 1];

    let src = `${imageHost}/${props.uri}${props.imageName.split('.')[0]}-${props.size}.${type}`;
    if (props?.preview) {
      src = `${process.env.NEXT_PUBLIC_PREVIEW_ASSET_HOST}/${props.uri}${props.imageName}`;
    } else if (props.raw || !props.uri) {
      src = `${imageHost}/${props.uri}${props.imageName}`;
    }

    const attributes = {
      'data-flickity-lazyload-src': !props.flickity ? undefined : src,
      alt: props.alt
    };

    if (props.dimensions) {
      attributes.height = props.dimensions.originalHeight;
      attributes.width = props.dimensions.originalWidth;
    }

    if (!props.disableLazyload) {
      attributes.className = props.flickity ? undefined : 'lazyload';
      attributes.src = 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mNkYAAAAAYAAjCB0C8AAAAASUVORK5CYII=';
      attributes['data-src'] = props.flickity ? undefined : src;
    } else {
      attributes.src = props.flickity ? undefined : src;
    }

    return (
      // eslint-disable-next-line
      <img {...attributes} />
    );
  };

  return (
    <StyledPicture {...props}>
      {!props?.preview
        && imageStack()}
      {defaultImage()}
    </StyledPicture>
  );
};

Picture.defaultProps = {
  theme: {},
  circle: false,
  imageName: '',
  types: [],
  alt: '',
  uri: '',
  raw: false,
  flickity: false,
  disableLazyload: false,
  size: 'large',
  dimensions: null
};

Picture.propTypes = {
  theme: PropTypes.shape(),
  circle: PropTypes.bool,
  imageName: PropTypes.string,
  types: PropTypes.array,
  alt: PropTypes.string,
  uri: PropTypes.string,
  raw: PropTypes.bool,
  flickity: PropTypes.bool,
  disableLazyload: PropTypes.bool,
  size: PropTypes.string,
  dimensions: PropTypes.object
};

export default withTheme(Picture);
